import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { semiFinal1Songs, autoQualifiersSF1 } from "../songs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn, getFlagUrl, getFlagGradient } from "@/lib/utils";
import type { Phase } from "@/types";

const allSongs = [...semiFinal1Songs, ...autoQualifiersSF1];

interface RoomProps {
  roomCode: string;
  users: string[];
  picks: Record<string, number[]>;
  currentUser: string;
  phase: Phase;
  lockedUsers: string[];
  isConnected?: boolean;
  onTogglePick: (songId: number) => void;
  onStartLocking: () => void;
  onLockFinal: () => void;
  onRevealAll: () => void;
  onResetRoom: () => void;
  onLeaveRoom: () => void;
}

export function Room({ 
  roomCode, 
  users, 
  picks, 
  currentUser, 
  phase, 
  lockedUsers,
  isConnected = true,
  onTogglePick, 
  onStartLocking,
  onLockFinal,
  onRevealAll,
  onResetRoom,
  onLeaveRoom
}: RoomProps) {
  const myPicks = picks[currentUser] || [];
  const isHost = users[0] === currentUser;
  const isLocked = lockedUsers.includes(currentUser);
  
  // Calculate how many users picked each song
  const songPickers: Record<number, string[]> = {};
  Object.entries(picks).forEach(([user, userPicks]) => {
    userPicks.forEach(songId => {
      if (!songPickers[songId]) {
        songPickers[songId] = [];
      }
      songPickers[songId].push(user);
    });
  });

  if (phase === "reveal") {
    return (
      <div className="space-y-8 animate-in fade-in zoom-in duration-1000">
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-3">
            <h2 className="text-5xl font-black euro-text-gradient italic uppercase tracking-tighter">The Big Reveal</h2>
            {!isConnected && (
              <Badge variant="destructive" className="animate-pulse text-[10px] uppercase h-5">
                Offline
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground uppercase tracking-widest text-sm">Compare everyone's final predictions</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.map(user => (
            <Card key={user} className="euro-glass border-white/10 overflow-hidden flex flex-col">
              <CardHeader className="bg-white/10 py-3 border-b border-white/10">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="font-black italic uppercase tracking-tighter">{user}</span>
                  {user === users[0] && <Badge variant="outline" className="text-[8px] border-primary/50 text-primary uppercase">Host</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3 flex-1">
                {(picks[user] || []).map(songId => {
                  const song = allSongs.find(s => s.id === songId);
                  const flagUrl = song ? getFlagUrl(song.country) : null;
                  return (
                    <div key={songId} className="flex items-center gap-3 p-1 rounded-lg bg-black/20 border border-white/5 overflow-hidden group hover:border-primary/30 transition-colors">
                      <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 bg-white/5 relative">
                        {song?.imageUrl ? (
                          <img src={song.imageUrl} alt={song.artist} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-muted-foreground">?</div>
                        )}
                        {flagUrl && (
                          <img src={flagUrl} className="absolute bottom-0 right-0 w-5 h-3.5 object-cover border border-black/50 shadow-sm" alt="" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p 
                            className="text-xs font-black uppercase truncate tracking-tight flex items-center gap-1"
                            style={{ 
                              backgroundImage: song ? getFlagGradient(song.country) : undefined,
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                            }}
                          >
                            {song?.country}
                          </p>
                          <span className="text-[9px] font-bold text-muted-foreground">#{song?.runningOrder}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground truncate italic">{song?.artist}</p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pt-8">
          {isHost && (
            <Button variant="destructive" onClick={onResetRoom} className="w-full sm:w-auto font-black uppercase tracking-tighter">
              Reset Draft for Everyone
            </Button>
          )}
          <Button variant="outline" onClick={onLeaveRoom} className="w-full sm:w-auto font-black uppercase tracking-tighter">
            Exit Room
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="euro-glass border-white/20">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <CardTitle className="text-2xl">Room: {roomCode}</CardTitle>
              {!isConnected && (
                <Badge variant="destructive" className="animate-pulse text-[10px] uppercase h-5">
                  Offline
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="capitalize">
                Phase: {phase}
              </Badge>
              {phase === 'drafting' && isHost && (
                <Button size="sm" variant="destructive" onClick={onStartLocking} className="h-7 text-[10px] uppercase font-bold tracking-tighter">
                  Start Locking Period
                </Button>
              )}
              {phase === 'locking' && isHost && (
                <Button size="sm" variant="destructive" onClick={onRevealAll} className="h-7 text-[10px] uppercase font-bold tracking-tighter euro-glow">
                  Reveal All
                </Button>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="flex flex-col items-end">
              <span className="text-2xl font-black euro-text-gradient">{myPicks.length}/10</span>
              <Button variant="link" size="sm" onClick={onLeaveRoom} className="h-4 p-0 text-[10px] uppercase text-muted-foreground hover:text-white">Exit Room</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {users.map((user, index) => {
              const userIsLocked = lockedUsers.includes(user);
              return (
                <Badge 
                  key={index} 
                  variant={user === currentUser ? "default" : "secondary"} 
                  className={`relative pr-6 ${userIsLocked ? 'bg-green-500/20 border-green-500/50 text-green-200' : ''}`}
                >
                  {user}
                  {index === 0 && <span className="ml-1 text-[8px] opacity-70">(Host)</span>}
                  {userIsLocked && <span className="absolute right-1 top-1/2 -translate-y-1/2">🔒</span>}
                </Badge>
              );
            })}
          </div>

          {phase === 'locking' && !isLocked && (
            <div className="pt-2">
              <Button 
                className="w-full euro-glow font-black uppercase tracking-tighter" 
                disabled={myPicks.length !== 10}
                onClick={onLockFinal}
              >
                Lock Final Predictions
              </Button>
            </div>
          )}

          {isLocked && (
            <div className="bg-green-500/20 border border-green-500/50 p-3 rounded-lg text-center animate-pulse">
              <p className="text-sm font-bold text-green-200">FINAL PREDICTIONS LOCKED 🔒</p>
              <p className="text-[10px] text-green-200/70">Waiting for other players...</p>
            </div>
          )}
        </CardContent>
      </Card>

      {phase === 'locking' && !isLocked && (
        <div className="bg-destructive/20 border border-destructive/50 p-4 rounded-lg text-center mb-4">
          <p className="font-bold text-destructive-foreground">LOCKING PHASE ACTIVE</p>
          <p className="text-xs text-muted-foreground">Your picks are now secret. Finalize your top 10!</p>
        </div>
      )}

      <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${isLocked ? 'opacity-50 pointer-events-none grayscale' : ''}`}>
        {allSongs.map((song) => {
          const isPicked = myPicks.includes(song.id);
          const othersWhoPicked = phase === 'drafting' 
            ? (songPickers[song.id] || []).filter(u => u !== currentUser)
            : [];
          const flagUrl = getFlagUrl(song.country);
          
          return (
            <Card 
              key={song.id} 
              className={`transition-all duration-500 overflow-hidden group border-0 bg-transparent shadow-2xl ${isPicked ? 'ring-4 ring-primary' : ''}`}
            >
              <div className="aspect-[2/3] w-full overflow-hidden relative rounded-2xl">
                {song.imageUrl ? (
                  <img src={song.imageUrl} alt={song.artist} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                ) : (
                  <div 
                    className="w-full h-full flex items-center justify-center font-black text-white/20 italic text-2xl uppercase tracking-widest"
                    style={{ background: getFlagGradient(song.country), opacity: 0.3 }}
                  >
                    {song.country}
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                
                {/* Flag in corner */}
                {flagUrl && (
                  <img src={flagUrl} className="absolute top-4 right-4 w-12 h-8 object-cover rounded shadow-lg border border-white/20" alt="" />
                )}

                <div className="absolute bottom-6 left-6 right-6 flex flex-col gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-black uppercase tracking-widest text-primary euro-glow px-2 py-0.5 bg-black/40 rounded">#{song.runningOrder}</span>
                    <Badge variant="outline" className="bg-white/10 backdrop-blur-md border-white/20 text-[10px] uppercase font-bold tracking-tighter h-auto py-1 px-2 leading-tight whitespace-pre-line max-w-[180px]">
                      {song.artist}
                    </Badge>
                  </div>
                  
                  <h3 
                    className="text-3xl sm:text-4xl font-black leading-[0.9] uppercase italic tracking-tighter bg-cover bg-center drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] drop-shadow-[0_0_15px_rgba(255,255,255,0.1)] break-words"
                    style={{ 
                      backgroundImage: getFlagGradient(song.country),
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {song.country}
                  </h3>
                  
                  <p className="text-sm font-bold text-white/70 italic truncate tracking-tight mt-1">{song.title}</p>
                  
                  <div className="mt-4 flex items-center justify-between gap-4">
                    <div className="flex -space-x-3 overflow-hidden">
                      {othersWhoPicked.map((picker, idx) => (
                        <div 
                          key={idx}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-full ring-2 ring-background bg-primary/20 backdrop-blur-sm text-xs font-black"
                          title={picker}
                        >
                          {picker.slice(0, 2).toUpperCase()}
                        </div>
                      ))}
                      {othersWhoPicked.length > 0 && (
                        <span className="ml-6 text-xs text-white/50 flex items-center font-black uppercase tracking-tighter">
                          +{othersWhoPicked.length}
                        </span>
                      )}
                    </div>
                    
                    <Button 
                      size="lg" 
                      variant={isPicked ? "default" : "secondary"}
                      className={cn(
                        "font-black uppercase tracking-tighter rounded-full px-8 h-12 transition-all duration-300",
                        isPicked ? "euro-glow scale-105" : "hover:bg-white hover:text-black"
                      )}
                      onClick={() => onTogglePick(song.id)}
                      disabled={(!isPicked && myPicks.length >= 10) || isLocked}
                    >
                      {isPicked ? "Selected" : "Add to Top 10"}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
